// خدمة رفع الصور إلى ImageKit و ImgBB

export interface ImageUploadResult {
  url: string;
  thumbnailUrl?: string;
  fileId?: string;
  fileName: string;
  size: number;
  provider: 'imagekit' | 'imgbb';
}

export interface ImageAnnotation {
  id: string;
  type: 'arrow' | 'text' | 'circle' | 'rectangle';
  x: number;
  y: number;
  width?: number;
  height?: number;
  text?: string;
  color: string;
  fontSize?: number;
  strokeWidth?: number;
  endX?: number; // للأسهم
  endY?: number; // للأسهم
}

export interface AnnotatedImage {
  originalUrl: string;
  annotatedUrl?: string;
  annotations: ImageAnnotation[];
  coilNumber: string;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * خدمة رفع الصور إلى ImageKit
 */
export class ImageKitService {
  private static readonly IMAGEKIT_ENDPOINT = 'https://upload.imagekit.io/api/v1/files/upload';

  // جلب مفاتيح ImageKit من الإعدادات أو متغيرات البيئة
  private static async getKeys() {
    try {
      console.log('🔑 جلب مفاتيح ImageKit...');

      // محاولة جلب الإعدادات من Firebase أولاً
      const { getMaintenanceBankSettings } = await import('./maintenanceBankSettingsService');
      const settings = await getMaintenanceBankSettings();

      const keys = {
        PUBLIC_KEY: settings.imagekitPublicKey || process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY || '',
        PRIVATE_KEY: settings.imagekitPrivateKey || process.env.REACT_APP_IMAGEKIT_PRIVATE_KEY || '',
        URL_ENDPOINT: settings.imagekitUrlEndpoint || process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT || ''
      };

      console.log('🔍 مصادر المفاتيح:', {
        publicFromFirebase: !!settings.imagekitPublicKey,
        privateFromFirebase: !!settings.imagekitPrivateKey,
        endpointFromFirebase: !!settings.imagekitUrlEndpoint,
        publicFromEnv: !!process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY,
        privateFromEnv: !!process.env.REACT_APP_IMAGEKIT_PRIVATE_KEY,
        endpointFromEnv: !!process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT
      });

      return keys;
    } catch (error) {
      console.warn('⚠️ تعذر جلب إعدادات ImageKit من Firebase، استخدام المتغيرات البيئية:', error);
      return {
        PUBLIC_KEY: process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY || '',
        PRIVATE_KEY: process.env.REACT_APP_IMAGEKIT_PRIVATE_KEY || '',
        URL_ENDPOINT: process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT || ''
      };
    }
  }

  static async uploadImage(file: File, fileName?: string): Promise<ImageUploadResult> {
    try {
      console.log('📤 بدء رفع الصورة إلى ImageKit...');
      const keys = await this.getKeys();

      // التحقق من وجود المفاتيح المطلوبة
      if (!keys.PUBLIC_KEY || !keys.PRIVATE_KEY || !keys.URL_ENDPOINT) {
        console.error('❌ مفاتيح ImageKit مفقودة:', {
          hasPublicKey: !!keys.PUBLIC_KEY,
          hasPrivateKey: !!keys.PRIVATE_KEY,
          hasUrlEndpoint: !!keys.URL_ENDPOINT
        });
        throw new Error('مفاتيح ImageKit غير مكتملة. يرجى التحقق من الإعدادات.');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileName', fileName || file.name);
      formData.append('publicKey', keys.PUBLIC_KEY);

      // إنشاء signature للمصادقة
      const timestamp = Date.now().toString();
      formData.append('timestamp', timestamp);

      // يجب إنشاء signature من الخادم في التطبيق الحقيقي
      // هنا نستخدم طريقة مبسطة للتجربة
      const signature = await this.generateSignature(timestamp, keys.PRIVATE_KEY);
      formData.append('signature', signature);

      console.log('🔑 تم إعداد البيانات للرفع:', {
        fileName: fileName || file.name,
        fileSize: file.size,
        publicKey: keys.PUBLIC_KEY.substring(0, 10) + '...'
      });

      console.log('📡 إرسال الطلب إلى ImageKit...');
      const response = await fetch(this.IMAGEKIT_ENDPOINT, {
        method: 'POST',
        body: formData,
      });

      console.log('📨 استجابة ImageKit:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ خطأ من ImageKit:', errorText);
        throw new Error(`ImageKit upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ نجح رفع الصورة إلى ImageKit:', {
        url: result.url,
        fileId: result.fileId,
        size: result.size
      });

      return {
        url: result.url,
        thumbnailUrl: result.thumbnailUrl,
        fileId: result.fileId,
        fileName: result.name,
        size: result.size,
        provider: 'imagekit'
      };
    } catch (error: any) {
      console.error('❌ خطأ في رفع الصورة إلى ImageKit:', error);

      // رسائل خطأ أكثر تفصيلاً
      if (error.message.includes('401')) {
        throw new Error('خطأ في المصادقة مع ImageKit. يرجى التحقق من المفاتيح.');
      } else if (error.message.includes('413')) {
        throw new Error('حجم الصورة كبير جداً. الحد الأقصى 5 ميجابايت.');
      } else if (error.message.includes('400')) {
        throw new Error('بيانات الطلب غير صحيحة. يرجى المحاولة مرة أخرى.');
      } else if (error.message.includes('network')) {
        throw new Error('مشكلة في الاتصال بالإنترنت. يرجى المحاولة مرة أخرى.');
      } else {
        throw new Error(`فشل في رفع الصورة إلى ImageKit: ${error.message}`);
      }
    }
  }

  private static async generateSignature(timestamp: string, privateKey: string): Promise<string> {
    // في التطبيق الحقيقي، يجب إنشاء هذا من الخادم
    // هنا نستخدم طريقة مبسطة للتجربة
    try {
      // استخدام crypto-js للتوقيع إذا كان متوفراً، وإلا استخدام طريقة بسيطة
      const signatureString = `timestamp=${timestamp}`;

      // محاولة استخدام HMAC SHA1 إذا كان متوفراً
      if (typeof window !== 'undefined' && (window as any).CryptoJS) {
        const CryptoJS = (window as any).CryptoJS;
        return CryptoJS.HmacSHA1(signatureString, privateKey).toString();
      }

      // طريقة بديلة بسيطة
      return btoa(`${timestamp}${privateKey}`);
    } catch (error) {
      console.warn('تعذر إنشاء التوقيع المتقدم، استخدام الطريقة البسيطة');
      return btoa(`${timestamp}${privateKey}`);
    }
  }

  static getOptimizedUrl(originalUrl: string, width?: number, height?: number, quality?: number): string {
    if (!originalUrl.includes('imagekit.io')) return originalUrl;
    
    const params = new URLSearchParams();
    if (width) params.append('tr', `w-${width}`);
    if (height) params.append('tr', `h-${height}`);
    if (quality) params.append('tr', `q-${quality}`);
    
    const separator = originalUrl.includes('?') ? '&' : '?';
    return `${originalUrl}${separator}${params.toString()}`;
  }
}

/**
 * خدمة رفع الصور إلى ImgBB
 */
export class ImgBBService {
  private static readonly IMGBB_ENDPOINT = 'https://api.imgbb.com/1/upload';

  // جلب مفتاح API من متغيرات البيئة أو الإعدادات
  private static getApiKey(): string {
    // أولاً محاولة جلب من متغيرات البيئة
    const envKey = process.env.REACT_APP_IMGBB_API_KEY;
    if (envKey && envKey.trim() !== '') {
      return envKey.trim();
    }

    // إذا لم يوجد، يمكن جلبه من localStorage (مؤقتاً)
    const storedKey = localStorage.getItem('imgbb_api_key');
    if (storedKey && storedKey.trim() !== '') {
      return storedKey.trim();
    }

    return '';
  }

  // دالة لتعيين مفتاح API (للاستخدام من الإعدادات)
  static setApiKey(apiKey: string): void {
    if (apiKey && apiKey.trim() !== '') {
      localStorage.setItem('imgbb_api_key', apiKey.trim());
    } else {
      localStorage.removeItem('imgbb_api_key');
    }
  }

  static async uploadImage(file: File): Promise<ImageUploadResult> {
    try {
      console.log('🔄 بدء رفع الصورة إلى ImgBB...');
      console.log('📁 معلومات الملف:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      const apiKey = this.getApiKey();
      if (!apiKey) {
        throw new Error('مفتاح ImgBB API غير موجود. يرجى إضافته في الإعدادات المتقدمة.');
      }

      console.log('🔑 مفتاح API موجود:', apiKey.substring(0, 8) + '...');

      const formData = new FormData();
      formData.append('image', file);
      formData.append('key', apiKey);

      console.log('📤 إرسال الطلب إلى ImgBB...');

      const response = await fetch(this.IMGBB_ENDPOINT, {
        method: 'POST',
        body: formData,
      });

      console.log('📥 استجابة ImgBB:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ خطأ في استجابة ImgBB:', errorText);
        throw new Error(`ImgBB upload failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📋 نتيجة ImgBB:', result);

      if (!result.success) {
        const errorMessage = result.error?.message || result.status_txt || 'ImgBB upload failed';
        console.error('❌ فشل رفع ImgBB:', errorMessage);
        throw new Error(errorMessage);
      }

      const uploadResult = {
        url: result.data.url,
        thumbnailUrl: result.data.thumb?.url,
        fileName: result.data.title || file.name,
        size: result.data.size || file.size,
        provider: 'imgbb' as const
      };

      console.log('✅ تم رفع الصورة بنجاح إلى ImgBB:', uploadResult.url);
      return uploadResult;

    } catch (error: any) {
      console.error('❌ خطأ في رفع الصورة إلى ImgBB:', error);

      // تحسين رسائل الخطأ
      let errorMessage = 'فشل في رفع الصورة إلى ImgBB';

      if (error.message.includes('API key')) {
        errorMessage = 'مفتاح ImgBB API غير صحيح أو غير موجود';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = 'مشكلة في الاتصال بالإنترنت';
      } else if (error.message.includes('413') || error.message.includes('too large')) {
        errorMessage = 'حجم الصورة كبير جداً';
      } else if (error.message.includes('400')) {
        errorMessage = 'تنسيق الصورة غير مدعوم';
      } else if (error.message.includes('429')) {
        errorMessage = 'تم تجاوز حد الرفع المسموح';
      }

      throw new Error(errorMessage);
    }
  }
}

/**
 * خدمة موحدة لرفع الصور
 */
export class ImageUploadService {
  static async uploadImage(
    file: File, 
    provider: 'imagekit' | 'imgbb' = 'imgbb',
    fileName?: string
  ): Promise<ImageUploadResult> {
    // التحقق من نوع الملف
    if (!file.type.startsWith('image/')) {
      throw new Error('يجب أن يكون الملف صورة');
    }

    // التحقق من حجم الملف (5MB كحد أقصى)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
    }

    try {
      switch (provider) {
        case 'imagekit':
          return await ImageKitService.uploadImage(file, fileName);
        case 'imgbb':
          return await ImgBBService.uploadImage(file);
        default:
          throw new Error('مزود الخدمة غير مدعوم');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      throw error;
    }
  }

  static async uploadMultipleImages(
    files: FileList | File[],
    provider: 'imagekit' | 'imgbb' = 'imgbb'
  ): Promise<ImageUploadResult[]> {
    const results: ImageUploadResult[] = [];
    const fileArray = Array.from(files);

    for (const file of fileArray) {
      try {
        const result = await this.uploadImage(file, provider);
        results.push(result);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        // يمكن إضافة معالجة خاصة للأخطاء هنا
      }
    }

    return results;
  }

  // دالة للحصول على الإعدادات المحفوظة مع دعم مصادر متعددة
  static async getStoredSettings(): Promise<{
    provider: 'imgbb' | 'imagekit';
    apiKey?: string;
    imagekitKeys?: {
      publicKey: string;
      privateKey: string;
      urlEndpoint: string;
    }
  }> {
    try {
      console.log('📖 جلب إعدادات رفع الصور من مصادر متعددة...');

      // محاولة جلب إعدادات بنك الصيانة أولاً
      try {
        const { getMaintenanceBankSettings } = await import('./maintenanceBankSettingsService');
        const maintenanceSettings = await getMaintenanceBankSettings();

        console.log('⚙️ إعدادات بنك الصيانة:', {
          provider: maintenanceSettings.imageProvider,
          hasImgbbKey: !!maintenanceSettings.imgbbApiKey,
          hasImagekitKeys: !!(maintenanceSettings.imagekitPublicKey && maintenanceSettings.imagekitPrivateKey && maintenanceSettings.imagekitUrlEndpoint)
        });

        // التحقق من اكتمال الإعدادات
        if (maintenanceSettings.imageProvider === 'imgbb' && maintenanceSettings.imgbbApiKey) {
          return {
            provider: 'imgbb',
            apiKey: maintenanceSettings.imgbbApiKey
          };
        } else if (maintenanceSettings.imageProvider === 'imagekit' &&
                   maintenanceSettings.imagekitPublicKey &&
                   maintenanceSettings.imagekitPrivateKey &&
                   maintenanceSettings.imagekitUrlEndpoint) {
          return {
            provider: 'imagekit',
            imagekitKeys: {
              publicKey: maintenanceSettings.imagekitPublicKey,
              privateKey: maintenanceSettings.imagekitPrivateKey,
              urlEndpoint: maintenanceSettings.imagekitUrlEndpoint
            }
          };
        }
      } catch (maintenanceError) {
        console.warn('⚠️ فشل جلب إعدادات بنك الصيانة:', maintenanceError);
      }

      // محاولة جلب إعدادات تخزين الصور كبديل
      try {
        const { getImageStorageSettings } = await import('./imageStorageService');
        const storageSettings = await getImageStorageSettings();

        console.log('⚙️ إعدادات تخزين الصور:', storageSettings);

        // تحديد المزود بناءً على نوع التخزين
        if (storageSettings.storageType.includes('imgbb')) {
          // محاولة جلب مفتاح ImgBB
          const { getImgBBSettings } = await import('./imgbbService');
          const imgbbSettings = await getImgBBSettings();

          if (imgbbSettings.apiKey) {
            return {
              provider: 'imgbb',
              apiKey: imgbbSettings.apiKey
            };
          }
        }

        if (storageSettings.storageType.includes('imagekit')) {
          // محاولة جلب إعدادات ImageKit
          const { getImageKitSettings } = await import('./imagekitService');
          const imagekitSettings = await getImageKitSettings();

          if (imagekitSettings.publicKey && imagekitSettings.privateKey && imagekitSettings.urlEndpoint) {
            return {
              provider: 'imagekit',
              imagekitKeys: {
                publicKey: imagekitSettings.publicKey,
                privateKey: imagekitSettings.privateKey,
                urlEndpoint: imagekitSettings.urlEndpoint
              }
            };
          }
        }
      } catch (storageError) {
        console.warn('⚠️ فشل جلب إعدادات تخزين الصور:', storageError);
      }

      // محاولة استخدام متغيرات البيئة كحل أخير
      console.log('🔄 استخدام متغيرات البيئة كحل أخير...');

      const envImgbbKey = process.env.REACT_APP_IMGBB_API_KEY;
      const envImagekitPublic = process.env.REACT_APP_IMAGEKIT_PUBLIC_KEY;
      const envImagekitPrivate = process.env.REACT_APP_IMAGEKIT_PRIVATE_KEY;
      const envImagekitEndpoint = process.env.REACT_APP_IMAGEKIT_URL_ENDPOINT;

      if (envImgbbKey) {
        console.log('✅ استخدام مفتاح ImgBB من متغيرات البيئة');
        return {
          provider: 'imgbb',
          apiKey: envImgbbKey
        };
      }

      if (envImagekitPublic && envImagekitPrivate && envImagekitEndpoint) {
        console.log('✅ استخدام مفاتيح ImageKit من متغيرات البيئة');
        return {
          provider: 'imagekit',
          imagekitKeys: {
            publicKey: envImagekitPublic,
            privateKey: envImagekitPrivate,
            urlEndpoint: envImagekitEndpoint
          }
        };
      }

      console.warn('⚠️ لم يتم العثور على إعدادات صالحة في أي مصدر');
      return { provider: 'imgbb' };

    } catch (error) {
      console.error('❌ خطأ في جلب الإعدادات:', error);
      return { provider: 'imgbb' };
    }
  }

  // دالة رفع ذكية تستخدم الإعدادات المحفوظة مع تبديل تلقائي
  static async smartUpload(file: File, fileName?: string): Promise<ImageUploadResult> {
    try {
      console.log('🧠 بدء الرفع الذكي...');
      console.log('📁 معلومات الملف:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      const settings = await this.getStoredSettings();
      console.log('⚙️ إعدادات الرفع:', {
        provider: settings.provider,
        hasApiKey: !!settings.apiKey,
        hasImagekitKeys: !!settings.imagekitKeys
      });

      // إعداد المفاتيح حسب المزود
      if (settings.provider === 'imgbb' && settings.apiKey) {
        ImgBBService.setApiKey(settings.apiKey);
        console.log('🔑 تم تعيين مفتاح ImgBB API');
      }

      try {
        // محاولة الرفع بالمزود المفضل
        console.log(`📤 محاولة الرفع باستخدام ${settings.provider}...`);
        return await this.uploadImage(file, settings.provider, fileName);
      } catch (primaryError: any) {
        console.warn(`⚠️ فشل الرفع باستخدام ${settings.provider}:`, primaryError.message);

        // التبديل التلقائي للمزود البديل
        const fallbackProvider = settings.provider === 'imagekit' ? 'imgbb' : 'imagekit';
        console.log(`🔄 محاولة الرفع باستخدام ${fallbackProvider}...`);

        try {
          // إعداد مفاتيح المزود البديل
          if (fallbackProvider === 'imgbb') {
            // محاولة استخدام مفتاح ImgBB من localStorage أو متغيرات البيئة
            const envKey = process.env.REACT_APP_IMGBB_API_KEY;
            const storedKey = localStorage.getItem('imgbb_api_key');
            const apiKey = storedKey || envKey;

            if (apiKey) {
              ImgBBService.setApiKey(apiKey);
              console.log('🔑 تم تعيين مفتاح ImgBB البديل');
            } else {
              throw new Error('مفتاح ImgBB غير متوفر للتبديل التلقائي');
            }
          }

          const result = await this.uploadImage(file, fallbackProvider, fileName);
          console.log(`✅ نجح الرفع باستخدام المزود البديل: ${fallbackProvider}`);
          return result;
        } catch (fallbackError: any) {
          console.error(`❌ فشل الرفع باستخدام المزود البديل ${fallbackProvider}:`, fallbackError.message);
          throw primaryError; // إرجاع الخطأ الأصلي
        }
      }
    } catch (error: any) {
      console.error('❌ فشل الرفع الذكي نهائياً:', error);
      throw error;
    }
  }

  // دالة رفع متوافقة مع نظام المنتجات (للاستخدام في بنك الصيانة)
  static async uploadForMaintenanceBank(file: File): Promise<ImageUploadResult> {
    try {
      console.log('🔧 رفع صورة لبنك الصيانة...');
      console.log('📁 معلومات الملف:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      // التحقق من صحة الملف أولاً
      const validation = this.validateImageFile(file);
      if (!validation.valid) {
        throw new Error(validation.error || 'ملف غير صالح');
      }

      // استخدام الخدمة الموحدة
      const { uploadUnifiedImage } = await import('./unifiedImageService');
      const result = await uploadUnifiedImage({
        file: file,
        context: 'maintenance-bank',
        folder: 'maintenance-bank'
      });

      console.log('✅ تم رفع الصورة بنجاح باستخدام الخدمة الموحدة:', result);

      // تحويل النتيجة إلى تنسيق ImageUploadResult
      return {
        url: result.url,
        thumbnailUrl: result.url, // نفس الرابط للمعاينة
        fileName: file.name,
        size: file.size,
        provider: result.provider
      };
    } catch (error: any) {
      console.error('❌ فشل رفع الصورة لبنك الصيانة:', error);
      
      // محاولة أخيرة باستخدام الرفع الذكي
      try {
        console.log('🔄 محاولة أخيرة باستخدام الرفع الذكي...');
        return await this.smartUpload(file);
      } catch (finalError: any) {
        console.error('❌ فشل نهائي في رفع الصورة:', finalError);
        throw new Error(`فشل في رفع الصورة: ${finalError.message}`);
      }
    }
  }

  static validateImageFile(file: File): { valid: boolean; error?: string } {
    // التحقق من نوع الملف
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: 'يجب أن يكون الملف صورة' };
    }

    // التحقق من حجم الملف
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { valid: false, error: 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت' };
    }

    // التحقق من أنواع الصور المدعومة
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!supportedTypes.includes(file.type)) {
      return { valid: false, error: 'نوع الصورة غير مدعوم. الأنواع المدعومة: JPEG, PNG, GIF, WebP' };
    }

    return { valid: true };
  }

  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // دالة اختبار سريع للإعدادات
  static async testSettings(): Promise<{ success: boolean; message: string; details: any }> {
    try {
      console.log('🧪 اختبار إعدادات رفع الصور...');

      const settings = await this.getStoredSettings();
      const details = {
        provider: settings.provider,
        hasImgbbKey: !!settings.apiKey,
        hasImagekitKeys: !!settings.imagekitKeys,
        imagekitKeysComplete: settings.imagekitKeys ?
          !!(settings.imagekitKeys.publicKey && settings.imagekitKeys.privateKey && settings.imagekitKeys.urlEndpoint) : false
      };

      console.log('📊 تفاصيل الاختبار:', details);

      // التحقق من الإعدادات حسب المزود
      if (settings.provider === 'imgbb') {
        if (!settings.apiKey) {
          return {
            success: false,
            message: 'مفتاح ImgBB API مفقود. يرجى إضافته في الإعدادات المتقدمة.',
            details
          };
        }
        return {
          success: true,
          message: 'إعدادات ImgBB جاهزة للاستخدام',
          details
        };
      } else if (settings.provider === 'imagekit') {
        if (!settings.imagekitKeys || !details.imagekitKeysComplete) {
          return {
            success: false,
            message: 'مفاتيح ImageKit غير مكتملة. يرجى إضافة جميع المفاتيح في الإعدادات المتقدمة.',
            details
          };
        }
        return {
          success: true,
          message: 'إعدادات ImageKit جاهزة للاستخدام',
          details
        };
      }

      return {
        success: false,
        message: 'مزود خدمة غير معروف',
        details
      };
    } catch (error: any) {
      console.error('❌ خطأ في اختبار الإعدادات:', error);
      return {
        success: false,
        message: `خطأ في اختبار الإعدادات: ${error.message}`,
        details: { error: error.message }
      };
    }
  }
}

/**
 * خدمة التعليق على الصور
 */
export class ImageAnnotationService {
  static generateAnnotationId(): string {
    return `annotation_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  static createArrowAnnotation(
    startX: number, 
    startY: number, 
    endX: number, 
    endY: number, 
    color: string = '#ff0000',
    strokeWidth: number = 3
  ): ImageAnnotation {
    return {
      id: this.generateAnnotationId(),
      type: 'arrow',
      x: startX,
      y: startY,
      endX,
      endY,
      color,
      strokeWidth
    };
  }

  static createTextAnnotation(
    x: number, 
    y: number, 
    text: string, 
    color: string = '#ff0000',
    fontSize: number = 16
  ): ImageAnnotation {
    return {
      id: this.generateAnnotationId(),
      type: 'text',
      x,
      y,
      text,
      color,
      fontSize
    };
  }

  static createCircleAnnotation(
    x: number, 
    y: number, 
    width: number, 
    height: number, 
    color: string = '#ff0000',
    strokeWidth: number = 3
  ): ImageAnnotation {
    return {
      id: this.generateAnnotationId(),
      type: 'circle',
      x,
      y,
      width,
      height,
      color,
      strokeWidth
    };
  }

  static createRectangleAnnotation(
    x: number, 
    y: number, 
    width: number, 
    height: number, 
    color: string = '#ff0000',
    strokeWidth: number = 3
  ): ImageAnnotation {
    return {
      id: this.generateAnnotationId(),
      type: 'rectangle',
      x,
      y,
      width,
      height,
      color,
      strokeWidth
    };
  }
}
